/**
 * QuestionPreview Component
 *
 * Renders a preview of a single question in the form builder.
 */

import React from 'react'
import { COUNTRY_CODES } from '../../constants/countryCodes'
import { getRatingPreset } from '../../constants/ratingPresets'

export default function QuestionPreview({
  question,
  index,
  questionNumber,
  isSelected,
  onClick,
  globalBgColor,
}) {
  const renderInput = () => {
    switch (question.type) {
      case 'text':
        return (
          <input
            type="text"
            placeholder={question.placeholder || 'Enter text...'}
            disabled
            className="preview-input"
          />
        )

      case 'textarea':
        return (
          <textarea
            placeholder={question.placeholder || 'Enter text...'}
            rows={question.rows || 4}
            disabled
            className="preview-input preview-textarea"
          />
        )

      case 'email':
        return (
          <input
            type="email"
            placeholder={question.placeholder || 'email@example.com'}
            disabled
            className="preview-input"
          />
        )

      case 'number':
        return (
          <input
            type="number"
            placeholder={question.placeholder || 'Enter number...'}
            min={question.min}
            max={question.max}
            disabled
            className="preview-input"
          />
        )

      case 'rating': {
        const style = question.ratingStyle || 'stars'
        const maxRating = question.maxRating || 5
        if (style === 'custom') {
          const icons = question.customRatingIcons || []
          return (
            <div className="preview-rating">
              {Array.from({ length: maxRating }, (_, i) =>
                icons[i] ? (
                  <img
                    key={i}
                    src={icons[i]}
                    alt={`Rating ${i + 1}`}
                    className="rating-custom-icon"
                  />
                ) : (
                  <span key={i} className="rating-star">
                    ☆
                  </span>
                )
              )}
            </div>
          )
        }
        const preset = getRatingPreset(style)
        return (
          <div className="preview-rating">
            {Array.from({ length: maxRating }, (_, i) => (
              <span key={i} className="rating-star" style={{ color: preset.unselectedColor }}>
                {preset.getIcon(i, maxRating, false)}
              </span>
            ))}
          </div>
        )
      }

      case 'select':
        return (
          <select disabled className="preview-input preview-select">
            <option value="">Select an option...</option>
            {(question.options || []).map((opt, i) => (
              <option key={i} value={opt}>
                {opt}
              </option>
            ))}
          </select>
        )

      case 'radio':
        return (
          <div className="preview-options">
            {(question.options || []).map((opt, i) => (
              <label key={i} className="preview-option">
                <input type="radio" name={`preview-${question.id}`} disabled />
                <span>{opt}</span>
              </label>
            ))}
          </div>
        )

      case 'checkbox':
        return (
          <div className="preview-options">
            {(question.options || []).map((opt, i) => (
              <label key={i} className="preview-option">
                <input type="checkbox" disabled />
                <span>{opt}</span>
              </label>
            ))}
          </div>
        )

      case 'open_response':
        return (
          <div className="preview-open-response">
            <div className="preview-response-tabs">
              <span className="preview-tab active">Text</span>
              <span className="preview-tab">Audio</span>
              <span className="preview-tab">Image</span>
            </div>
            <textarea
              placeholder="Type your response..."
              rows={3}
              disabled
              className="preview-input preview-textarea"
            />
          </div>
        )

      case 'phone': {
        const selectedCode = question.countryCode || '+91'
        const country = COUNTRY_CODES.find((c) => c.dialCode === selectedCode)
        return (
          <div
            className="preview-phone"
            style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
            <select disabled className="preview-input" style={{ width: 'auto', minWidth: '120px' }}>
              <option>{country ? `${country.code} ${country.dialCode}` : selectedCode}</option>
            </select>
            <input
              type="tel"
              placeholder={question.placeholder || 'Phone number...'}
              disabled
              className="preview-input"
              style={{ flex: 1 }}
            />
          </div>
        )
      }

      default:
        return <p className="preview-unknown">Unknown question type</p>
    }
  }

  // Layout elements render differently
  if (question.type === 'section_header') {
    return (
      <div
        className={`question-preview preview-section-header ${isSelected ? 'selected' : ''}`}
        onClick={onClick}>
        <h3 className="section-header-title">{question.label || 'Section Header'}</h3>
        {question.description && <p className="section-header-desc">{question.description}</p>}
      </div>
    )
  }

  if (question.type === 'divider') {
    return (
      <div
        className={`question-preview preview-divider ${isSelected ? 'selected' : ''}`}
        onClick={onClick}>
        <hr className="section-divider-line" />
      </div>
    )
  }

  // Effective background: per-question overrides global
  const bgColor = question.backgroundColor || globalBgColor || null
  const bgStyle = bgColor ? { backgroundColor: bgColor } : {}

  return (
    <div
      className={`question-preview ${isSelected ? 'selected' : ''}`}
      onClick={onClick}
      style={bgStyle}>
      <label className="preview-label">
        <span className="question-num">{questionNumber ?? index + 1}.</span>
        {question.label || 'Untitled Question'}
        {question.required && <span className="required-mark">*</span>}
      </label>
      {renderInput()}
    </div>
  )
}
